import React, { useState, useEffect } from 'react'
import {
  Button,
  Loader,
  AlertBanner,
  AlertBannerText,
  Flex,
  Text,
  Box,
} from '@vibe/core'
import { apiService } from '../../services/api'
import { getErrorMessage } from '../../utils/errorHandling'

interface CVViewerProps {
  candidateId: string
  cvUrl?: string
  filename?: string
}

/**
 * Component for secure CV document viewing with presigned URL handling
 */
export const CVViewer: React.FC<CVViewerProps> = ({
  candidateId,
  cvUrl: providedCvUrl,
  filename: providedFilename,
}) => {
  const [cvUrl, setCvUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filename, setFilename] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<string | null>(null)

  useEffect(() => {
    if (providedCvUrl) {
      // Use the provided CV URL directly
      setCvUrl(providedCvUrl)
      setFilename(providedFilename || null)

      // Extract expiration from the presigned URL if available
      try {
        const url = new URL(providedCvUrl)
        const expires = url.searchParams.get('Expires')
        if (expires) {
          setExpiresAt(new Date(parseInt(expires) * 1000).toISOString())
        }
      } catch (urlError) {
        console.warn('Could not parse expiration from URL:', urlError)
      }

      setLoading(false)
    } else {
      loadCVUrl()
    }
  }, [candidateId, providedCvUrl, providedFilename])

  const loadCVUrl = async () => {
    try {
      setLoading(true)
      setError(null)

      // First try to get the candidate detail which includes the url_visor
      const candidateResponse = await apiService.getCandidateDetail(candidateId)
      const candidate = candidateResponse.data

      if (candidate.url_visor) {
        setCvUrl(candidate.url_visor)
        setFilename(candidate.cv_filename || null)
        // Extract expiration from the presigned URL if available
        try {
          const url = new URL(candidate.url_visor)
          const expires = url.searchParams.get('Expires')
          if (expires) {
            setExpiresAt(new Date(parseInt(expires) * 1000).toISOString())
          }
        } catch (urlError) {
          console.warn('Could not parse expiration from URL:', urlError)
        }
      } else {
        // Fallback to the original getCVUrl method if url_visor is not available
        const response = await apiService.getCVUrl(candidateId)
        setCvUrl(response.data.cv_url)
        setFilename(response.data.filename || null)
        setExpiresAt(response.data.expires_at)
      }
    } catch (err) {
      console.error('Error loading CV URL:', err)
      setError(getErrorMessage(err) || 'Error al cargar el CV del candidato')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    // Security validation before download
    if (!validateCVAccess()) {
      return
    }

    try {
      // Create a temporary link to download the file
      const link = document.createElement('a')
      link.href = cvUrl!
      link.download = filename || 'cv.pdf'
      link.target = '_blank'
      link.rel = 'noopener noreferrer' // Security: Prevent window.opener access
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error('Error during download:', err)
      setError(
        getErrorMessage(err) || 'Error al descargar el CV. Intenta nuevamente.'
      )
    }
  }

  const handleRefresh = () => {
    loadCVUrl()
  }

  const isExpired = () => {
    if (!expiresAt) return false
    return new Date() > new Date(expiresAt)
  }

  const validateCVAccess = () => {
    // Security validation: Ensure user has proper access to this CV
    if (!candidateId) {
      setError('ID de candidato no válido')
      return false
    }

    if (!cvUrl) {
      setError('URL del CV no disponible')
      return false
    }

    if (isExpired()) {
      setError('El enlace del CV ha expirado')
      return false
    }

    // Security validation: Ensure URL is from a trusted source (S3)
    try {
      const url = new URL(cvUrl)
      if (
        !url.hostname.includes('amazonaws.com') &&
        !url.hostname.includes('s3')
      ) {
        setError('URL del CV no es de una fuente confiable')
        return false
      }
    } catch (err) {
      setError('URL del CV no es válida')
      return false
    }

    return true
  }

  if (loading) {
    return (
      <Box className="p-8">
        <Flex
          direction="column"
          align="center"
          justify="center"
          className="min-h-64"
        >
          <Loader size="large" />
          <Text className="mt-4">Cargando CV...</Text>
        </Flex>
      </Box>
    )
  }

  if (error) {
    return (
      <Box className="p-4">
        <AlertBanner backgroundColor="negative" className="mb-4">
          <AlertBannerText text={error} />
        </AlertBanner>
        <Button onClick={handleRefresh} kind="secondary" size="small">
          Reintentar
        </Button>
      </Box>
    )
  }

  if (!cvUrl) {
    return (
      <Box className="p-4">
        <AlertBanner backgroundColor="warning">
          <AlertBannerText text="CV no disponible para este candidato" />
        </AlertBanner>
      </Box>
    )
  }

  if (isExpired()) {
    return (
      <Box className="p-4">
        <AlertBanner backgroundColor="warning" className="mb-4">
          <AlertBannerText text="El enlace del CV ha expirado. Haz clic en 'Actualizar' para generar un nuevo enlace." />
        </AlertBanner>
        <Button onClick={handleRefresh} kind="primary" size="small">
          Actualizar
        </Button>
      </Box>
    )
  }

  return (
    <div className="space-y-4" data-testid="cv-viewer">
      {/* CV Controls */}
      <Flex
        justify="space-between"
        align="center"
        className="p-4 bg-gray-50 rounded-lg"
      >
        <div>
          <Text weight="medium" className="mb-1">
            {filename || 'CV del Candidato'}
          </Text>
        </div>

        <Flex gap="small">
          <Button onClick={handleDownload} kind="secondary" size="small">
            Descargar
          </Button>
        </Flex>
      </Flex>

      {/* PDF Viewer */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <iframe
          src={cvUrl}
          title="CV del Candidato"
          className="w-full h-screen min-h-96"
          style={{ minHeight: '600px' }}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          referrerPolicy="strict-origin-when-cross-origin"
          onError={() => {
            setError(
              'Error al cargar el visor de PDF. Intenta descargar el archivo.'
            )
          }}
          onLoad={() => {
            // Security validation: Check if URL is still valid after load
            if (isExpired()) {
              setError('El enlace del CV ha expirado durante la carga.')
            }
          }}
        />
      </div>

      {/* Fallback message */}
      <Box className="p-4 bg-blue-50 rounded-lg">
        <Text type="text3" color="secondary">
          Si el CV no se muestra correctamente, puedes descargarlo usando el
          botón "Descargar" arriba.
        </Text>
      </Box>
    </div>
  )
}
